import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { Copy, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { PUBLIC_SITE_ORIGIN } from "@/lib/publicSite";

interface Props { open: boolean; onOpenChange: (v: boolean) => void; }

export default function InviteDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const url = PUBLIC_SITE_ORIGIN;
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t("invite.copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed"); }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "My Tennis Ledger",
          text: t("invite.shareText"),
          url,
        });
      } catch {}
    } else {
      copy();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("invite.title")}</DialogTitle>
          <DialogDescription>{t("invite.subtitle")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-4">
          <div className="p-4 bg-white rounded-lg shadow-glow">
            <QRCodeSVG value={url} size={200} level="M" />
          </div>
          <div className="w-full">
            <Input value={url} readOnly className="font-mono text-xs text-center" />
          </div>
          <div className="flex gap-2 w-full">
            <Button onClick={copy} variant="outline" className="flex-1">
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {t("invite.copyLink")}
            </Button>
            <Button onClick={share} className="flex-1 bg-gradient-primary text-primary-foreground">
              <Share2 className="w-4 h-4 mr-2" />
              {t("invite.share")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
